import axios from 'axios';

/**
 * Shared axios instance for all backend API calls.
 *
 * Request interceptor: when the app runs inside Shopify Admin, calls
 * window.shopify.idToken() (App Bridge v4 CDN API) and attaches the
 * resulting JWT as `Authorization: Bearer <token>`.
 *
 * Outside Shopify Admin the call either fails silently or is skipped,
 * so requests fall through without an auth header — existing store_id
 * query-param auth continues to work unchanged.
 *
 * The awkward fact this file exists to handle: App Bridge does not mint a
 * token on demand. It returns the *current* one and only replaces it once
 * that has actually expired. Measured in the running app, idToken() handed
 * back a token with zero seconds of life left, and produced a fresh 60-second
 * one only after the old had died. So "ask for a token" is not the same as
 * "get a usable token", and any code that assumes otherwise sends expired
 * JWTs for the last stretch of every token's life.
 *
 * That is what the backend was rejecting with ExpiredSignatureError. Rare per
 * request, but a product sync polls every two seconds for half an hour and
 * one refusal aborted the whole run.
 *
 * The rule here is therefore about usability, never about identity: the same
 * token returned twice is fine if it still has life; a brand-new one with a
 * second left is not.
 */
const api = axios.create();

// Below this, a token is not worth sending: the request would very likely be
// refused, and refusing it here is cheaper than a round trip plus a retry.
// Comfortably above normal request latency.
const MIN_USABLE_MS = 5_000;
// Above this, renew ahead of time without making anyone wait. Wider than
// MIN_USABLE_MS on purpose — renewing early is free, waiting is not.
const RENEW_MARGIN_MS = 10_000;
// Enough to cover the handover window; bounded so the app cannot hang if App
// Bridge stops producing tokens altogether.
const MAX_TOKEN_ATTEMPTS = 3;
// Added to the wait so we ask *after* the old token has expired, which is
// when App Bridge replaces it — not a moment before.
const WAIT_BUFFER_MS = 750;
// For a token whose payload cannot be read. Short: not knowing is a reason to
// ask again soon, not a reason to trust it longer.
const FALLBACK_TTL_MS = 30_000;

let _cachedToken = null;
let _tokenExpiry = 0;
// The in-flight acquisition, shared by every caller that arrives while it is
// running. It matters more now that an acquisition can wait for seconds:
// without it, five requests would each start their own wait.
let _inflight = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Milliseconds of life left in this token, or null if that cannot be read.
 *
 * The payload is decoded, not verified: proving the signature is the
 * backend's job and would prove nothing here. All we need is `exp`.
 */
function remainingMs(token) {
  try {
    const payload = String(token).split('.')[1];
    if (!payload) return null;
    // JWT uses base64url and omits padding; atob wants neither.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const { exp } = JSON.parse(atob(padded));
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
    return exp * 1000 - Date.now();
  } catch (_) {
    return null;
  }
}

/**
 * An unreadable token counts as usable: we have no evidence against it, and
 * refusing everything we cannot parse would take the app down the day Shopify
 * changes the token format. It is cached briefly instead.
 */
function isUsable(token) {
  if (!token) return false;
  const remaining = remainingMs(token);
  return remaining === null || remaining >= MIN_USABLE_MS;
}

/**
 * Keep asking App Bridge until it yields a token worth sending, or give up.
 *
 * The wait between attempts comes from the token itself. Knowing App Bridge
 * replaces a token once it expires, and knowing from `exp` how long that is,
 * the right moment to ask again is computable rather than guessable — a fixed
 * delay would be either too short to help or longer than necessary.
 */
async function acquireToken() {
  for (let attempt = 1; attempt <= MAX_TOKEN_ATTEMPTS; attempt++) {
    let token = null;
    try {
      token = await window.shopify.idToken();
    } catch (_) {
      token = null;      // App Bridge unreachable this time; the retry covers it
    }

    if (token) {
      const remaining = remainingMs(token);
      if (remaining === null || remaining >= MIN_USABLE_MS) {
        _cachedToken = token;
        _tokenExpiry = remaining === null
          ? Date.now() + FALLBACK_TTL_MS
          : Date.now() + remaining - RENEW_MARGIN_MS;
        return token;
      }
      if (attempt < MAX_TOKEN_ATTEMPTS) {
        await sleep(Math.max(remaining, 0) + WAIT_BUFFER_MS);
      }
      continue;
    }

    if (attempt < MAX_TOKEN_ATTEMPTS) await sleep(WAIT_BUFFER_MS);
  }

  // Nothing usable. Say so plainly rather than leaving a stale token behind
  // for the next request to send.
  _cachedToken = null;
  _tokenExpiry = 0;
  return null;
}

function fetchToken() {
  if (_inflight) return _inflight;
  _inflight = acquireToken();
  return _inflight.finally(() => { _inflight = null; });
}

function attachAuth(config, token) {
  if (typeof config.headers?.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
}

function noUsableToken() {
  const err = new Error('No usable Shopify session token');
  err.code = 'ERR_NO_SESSION_TOKEN';
  return err;
}

api.interceptors.request.use(async (config) => {
  // Not embedded in Shopify Admin: unchanged behaviour, no header, no error.
  if (!window.shopify?.idToken) return config;

  let token = _cachedToken;
  if (!token || Date.now() >= _tokenExpiry) {
    token = await fetchToken();
  }

  // The check that was missing, and the whole point of this file: having a
  // token is not the same as having one worth sending.
  if (isUsable(token)) {
    attachAuth(config, token);
    return config;
  }
  throw noUsableToken();
});

// Recovery after a 401: App Bridge does not mint a token on demand, so a
// single fetchToken() pass right after a rejection can hand back the exact
// token that was just refused (it hasn't rotated yet). This bounds how many
// times we ask again before giving up — each round already retries
// internally via acquireToken(), so the real budget is this multiplied by
// MAX_TOKEN_ATTEMPTS, not an unbounded wait.
const RECOVERY_MAX_ROUNDS = 3;

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err?.response?.status !== 401) return Promise.reject(err);

    // The cached token is demonstrably not accepted; drop it whatever else
    // we decide to do — but remember it, so the retry can refuse to send
    // this exact one back even if App Bridge hands it out again.
    const rejectedToken = _cachedToken;
    _cachedToken = null;
    _tokenExpiry = 0;

    const config = err.config;
    // One retry, exactly, of the original request. The flag lives on the
    // request's own config, so a second 401 on the same request goes
    // straight back to the caller and there is no way to loop. Only 401 is
    // retried: it is the one status the backend returns from an auth
    // dependency, before the handler runs, so replaying the request cannot
    // repeat a side effect — which is what makes this safe on
    // POST /shopify/sync-products and the other writes.
    if (!config || config.__retried || !window.shopify?.idToken) {
      return Promise.reject(err);
    }
    config.__retried = true;

    // Ask across a few rounds rather than once: App Bridge may still be
    // holding the same expired token for a beat after Shopify has stopped
    // accepting it, and a token equal to the one just rejected is never
    // acceptable here regardless of what its own exp claims locally.
    let token = null;
    for (let round = 0; round < RECOVERY_MAX_ROUNDS; round++) {
      const candidate = await fetchToken();
      if (candidate && candidate !== rejectedToken && isUsable(candidate)) {
        token = candidate;
        break;
      }
      if (round < RECOVERY_MAX_ROUNDS - 1) await sleep(WAIT_BUFFER_MS);
    }
    if (!token) return Promise.reject(err);

    // Re-issuing through `api` runs the request interceptor again, which
    // overwrites Authorization with the token just validated. The stale
    // header from the first attempt never survives.
    return api(config);
  }
);

export default api;
